import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

const SEPARATOR = ', ';

let measureCanvas = null;
function getMeasureContext(font) {
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  ctx.font = font;
  return ctx;
}

function textWidth(text, font) {
  return getMeasureContext(font).measureText(text).width;
}

/**
 * Calcula o label de um trigger multi-select mostrando o máximo de opções
 * selecionadas que couberem na largura disponível do botão, seguido de
 * "+N" para o restante. Mede o texto com a fonte real do elemento via
 * canvas, recalculando quando o botão for redimensionado.
 *
 * @param {import('vue').Ref<HTMLElement|null>} triggerRef - elemento do botão trigger
 * @param {import('vue').Ref<string[]>} selectedLabels - labels das opções selecionadas, na ordem de seleção
 * @param {import('vue').Ref<number>|number} [reservedWidth] - espaço reservado (ícone/chevron), em px
 * @returns {import('vue').ComputedRef<string>}
 */
export function useMultiSelectLabel(
  triggerRef,
  selectedLabels,
  reservedWidth = 28
) {
  const availableWidth = ref(0);
  let resizeObserver = null;

  function measureAvailableWidth() {
    if (!triggerRef.value) return;
    const reserved =
      typeof reservedWidth === 'object' ? reservedWidth.value : reservedWidth;
    availableWidth.value = triggerRef.value.offsetWidth - reserved;
  }

  function observe(el) {
    if (resizeObserver) resizeObserver.disconnect();
    if (el && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(measureAvailableWidth);
      resizeObserver.observe(el);
    }
    measureAvailableWidth();
  }

  onMounted(() => {
    observe(triggerRef.value);
    requestAnimationFrame(() => nextTick(measureAvailableWidth));
  });

  onUnmounted(() => {
    if (resizeObserver) resizeObserver.disconnect();
  });

  watch(triggerRef, el => observe(el));

  watch(selectedLabels, () => nextTick(measureAvailableWidth));

  return computed(() => {
    const labels = selectedLabels.value;
    if (!labels.length) return '';
    if (labels.length === 1) return labels[0];

    const font = triggerRef.value
      ? window.getComputedStyle(triggerRef.value).font
      : '400 14px sans-serif';
    const width = availableWidth.value;

    let shown = 0;
    let current = '';
    for (let i = 0; i < labels.length; i += 1) {
      const remaining = labels.length - (i + 1);
      const candidate = current
        ? `${current}${SEPARATOR}${labels[i]}`
        : labels[i];
      const suffixed = remaining > 0 ? `${candidate} +${remaining}` : candidate;
      if (textWidth(suffixed, font) <= width || shown === 0) {
        current = candidate;
        shown += 1;
      } else {
        break;
      }
    }

    const hidden = labels.length - shown;
    return hidden > 0 ? `${current} +${hidden}` : current;
  });
}
